import emailjs from '@emailjs/browser';
import { EMAILJS_CONFIG } from '../config/emailjs';

// Initialize EmailJS
emailjs.init(EMAILJS_CONFIG.PUBLIC_KEY);

export interface ServiceApplicationData {
  serviceName: string;
  applicantName: string;
  applicantEmail: string;
  applicantPhone?: string;
  company?: string;
  message?: string;
}

export interface JobApplicationData {
  jobTitle: string;
  applicantName: string;
  applicantEmail: string;
  applicantPhone: string;
  resume: string;
  positionType: 'Full-time' | 'Part-time';
  coverLetter?: string;
}

export interface ContactFormData {
  name: string;
  email: string;
  company?: string;
  phone?: string;
  service: string;
  message: string;
}

export const sendServiceApplication = async (data: ServiceApplicationData): Promise<boolean> => {
  try {
    const templateParams = {
      to_email: EMAILJS_CONFIG.TO_EMAIL,
      service_name: data.serviceName,
      applicant_name: data.applicantName,
      applicant_email: data.applicantEmail,
      applicant_phone: data.applicantPhone || 'Not provided',
      company: data.company || 'Not provided',
      message: data.message || `Application for ${data.serviceName}`,
      from_name: data.applicantName,
      reply_to: data.applicantEmail,
    };

    const result = await emailjs.send(
      EMAILJS_CONFIG.SERVICE_ID,
      EMAILJS_CONFIG.SERVICE_TEMPLATE_ID,
      templateParams
    );

    console.log('Service application email sent successfully:', result);
    return true;
  } catch (error) {
    console.error('Error sending service application email:', error);
    return false;
  }
};

export const sendJobApplication = async (data: JobApplicationData): Promise<boolean> => {
  try {
    const templateParams = {
      to_email: EMAILJS_CONFIG.TO_EMAIL,
      job_title: data.jobTitle,
      applicant_name: data.applicantName,
      applicant_email: data.applicantEmail,
      applicant_phone: data.applicantPhone,
      resume: data.resume,
      position_type: data.positionType,
      cover_letter: data.coverLetter || 'No cover letter provided',
      from_name: data.applicantName,
      reply_to: data.applicantEmail,
    };

    const result = await emailjs.send(
      EMAILJS_CONFIG.SERVICE_ID,
      EMAILJS_CONFIG.SERVICE_TEMPLATE_ID,
      templateParams
    );

    console.log('Job application email sent successfully:', result);
    return true;
  } catch (error) {
    console.error('Error sending job application email:', error);
    return false;
  }
};

export const sendContactForm = async (data: ContactFormData): Promise<boolean> => {
  try {
    const templateParams = {
      to_email: EMAILJS_CONFIG.TO_EMAIL,
      from_name: data.name,
      from_email: data.email,
      company: data.company || 'Not provided',
      phone: data.phone || 'Not provided',
      service: data.service || 'General Inquiry',
      message: data.message,
      reply_to: data.email,
    };

    const result = await emailjs.send(
      EMAILJS_CONFIG.SERVICE_ID,
      EMAILJS_CONFIG.CONTACT_TEMPLATE_ID,
      templateParams
    );

    console.log('Contact form email sent successfully:', result);
    return true;
  } catch (error) {
    console.error('Error sending contact form email:', error);
    return false;
  }
};

// Fallback function to send emails using mailto (if EmailJS fails)
export const sendEmailFallback = (data: ServiceApplicationData | ContactFormData | JobApplicationData, type: 'service' | 'contact' | 'job') => {
  let subject: string;
  let body: string;

  if (type === 'service') {
    const serviceData = data as ServiceApplicationData;
    subject = `Service Application: ${serviceData.serviceName}`;
    body = `
Service Application Details:
- Service: ${serviceData.serviceName}
- Name: ${serviceData.applicantName}
- Email: ${serviceData.applicantEmail}
- Phone: ${serviceData.applicantPhone || 'Not provided'}
- Company: ${serviceData.company || 'Not provided'}
- Message: ${serviceData.message || 'No additional message'}

Please contact this applicant at: ${serviceData.applicantEmail}
    `;
  } else if (type === 'job') {
    const jobData = data as JobApplicationData;
    subject = `Job Application: ${jobData.jobTitle}`;
    body = `
Job Application Details:
- Job Title: ${jobData.jobTitle}
- Name: ${jobData.applicantName}
- Email: ${jobData.applicantEmail}
- Phone: ${jobData.applicantPhone}
- Position Type: ${jobData.positionType}
- Resume: ${jobData.resume}
- Cover Letter: ${jobData.coverLetter || 'No cover letter provided'}

Please contact this applicant at: ${jobData.applicantEmail}
    `;
  } else {
    const contactData = data as ContactFormData;
    subject = `Contact Form: ${contactData.service}`;
    body = `
Contact Form Details:
- Name: ${contactData.name}
- Email: ${contactData.email}
- Company: ${contactData.company || 'Not provided'}
- Phone: ${contactData.phone || 'Not provided'}
- Service Interest: ${contactData.service}
- Message: ${contactData.message}

Please contact this person at: ${contactData.email}
    `;
  }

  const mailtoLink = `mailto:${EMAILJS_CONFIG.TO_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  window.open(mailtoLink);
};
